import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import OutlinedInput from "@mui/material/OutlinedInput";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";

const RecordingsForm = ({ state, headerNames, errors, updateField, productions, existingRecordings }) => {
  const [selectedProductionId, setSelectedProductionId] = useState("");

  // Sync local state with parent state
  useEffect(() => {
    if (state?.p_id) {
      // Ensure we store the ID as-is (number) to match MenuItem values
      setSelectedProductionId(state.p_id);
    } else {
      setSelectedProductionId("");
    }
  }, [state?.p_id]);

  // Find the selected production object for episode calculations
  const selectedProduction = selectedProductionId && productions
    ? productions.find((p) => p.id === selectedProductionId)
    : null;

  // Get all available episodes for the production
  const allEpisodes = selectedProduction?.no_of_episodes
    ? Array.from({ length: selectedProduction.no_of_episodes }, (_, i) => i + 1)
    : [];

  // Calculate which episodes are already used in other recordings (excluding current recording)
  const usedEpisodes = React.useMemo(() => {
    if (!existingRecordings || !Array.isArray(existingRecordings) || !state?.p_id) {
      return new Set();
    }
    
    const used = new Set();
    existingRecordings.forEach((recording) => {
      // Exclude current recording if editing
      if (state?.id && recording.id === state.id) {
        return;
      }
      // Add all episodes from this recording to the used set
      if (Array.isArray(recording.episodes)) {
        recording.episodes.forEach((ep) => used.add(ep));
      }
    });
    return used;
  }, [existingRecordings, state?.id, state?.p_id]);


  const handleProductionChange = (e) => {
    const productionId = e.target.value;
    setSelectedProductionId(productionId);
    
    // Handle empty string - set to null
    if (productionId === "" || productionId === null || productionId === undefined) {
      updateField("p_id", null);
      updateField("episodes", []);
      return;
    }
    
    // Convert to number for storage (validation expects a number)
    const productionIdNum = Number(productionId);
    updateField("p_id", productionIdNum);
    // Clear episodes when production changes
    updateField("episodes", []);
  };

  const handleEpisodesChange = (e) => {
    const value = e.target.value;
    const sortedValue = typeof value === "string"
      ? value.split(",").map(Number).sort((a, b) => a - b)
      : [...value].sort((a, b) => a - b);
    updateField("episodes", sortedValue);
  };

  const handleDateChange = (newValue) => {
    updateField("rec_date", newValue ? newValue.toISOString() : null);
  };

  return (
    <Box p={3} sx={{ maxWidth: 600, margin: '0 auto' }}>
        <Grid container spacing={3}>
          {/* Production Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small" error={!!errors?.p_id}>
              <InputLabel id="production-select-label">{headerNames.p_id || "Παραγωγή"}</InputLabel>
              <Select
                labelId="production-select-label"
                id="production-select"
                label={headerNames.p_id || "Παραγωγή"}
                value={selectedProductionId}
                onChange={handleProductionChange}
                disabled={!!state?.id} // Disable if editing (can't change production)
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {!productions || !Array.isArray(productions) || productions.length === 0 ? (
                  <MenuItem disabled value="">
                    <em>Δεν υπάρχουν παραγωγές</em>
                  </MenuItem>
                ) : (
                  productions.map((production) => (
                    <MenuItem key={production.id} value={production.id}>
                      {production.p_descr}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors?.p_id && (
                <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                  {errors.p_id}
                </div>
              )}
            </FormControl>
          </Grid>

          {/* Recording Number */}
          <Grid item xs={12}>
            <TextField
              id="rec_number-id"
              label={headerNames.rec_number || "Αριθμός Εγγραφής"}
              size="small"
              fullWidth
              type="number"
              value={state?.rec_number || ""}
              onChange={(e) => updateField("rec_number", parseInt(e.target.value) || 0)}
              helperText={errors?.rec_number || ""}
              error={!!errors?.rec_number}
              InputProps={{ inputMode: "numeric", min: 1 }}
            />
          </Grid>

          {/* Recording Date */}
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer sx={{ paddingBottom: 1 }} components={['DatePicker']}>
                <DatePicker
                  label={headerNames.rec_date || "Ημερομηνία Εγγραφής"}
                  value={state?.rec_date ? dayjs(state.rec_date) : null}
                  onChange={handleDateChange}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: !!errors?.rec_date,
                      helperText: errors?.rec_date || "",
                    },
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>

          {/* Episodes Selection */}
          {selectedProduction && allEpisodes.length > 0 && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!errors?.episodes}>
                <InputLabel id="episodes-label">{headerNames.episodes || "Επεισόδια"}</InputLabel>
                <Select
                  multiple
                  labelId="episodes-label"
                  value={Array.isArray(state?.episodes) ? state.episodes : []}
                  onChange={handleEpisodesChange}
                  input={<OutlinedInput label={headerNames.episodes || "Επεισόδια"} />}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) return '';
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.sort((a, b) => a - b).map((value) => (
                          <Chip key={value} label={`E-${value}`} size="small" />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        overflowY: "auto",
                      },
                    },
                  }}
                >
                  {allEpisodes.map((episode) => {
                    const isUsed = usedEpisodes.has(episode);
                    const isSelected = Array.isArray(state?.episodes) && state.episodes.indexOf(episode) > -1;
                    // Allow if not used, or if already selected in current recording (for editing)
                    const isAvailable = !isUsed || isSelected;
                    
                    return (
                      <MenuItem 
                        key={episode} 
                        value={episode}
                        disabled={!isAvailable}
                        sx={{
                          opacity: isUsed && !isSelected ? 0.5 : 1,
                          '&.Mui-disabled': {
                            opacity: 0.5,
                          },
                        }}
                      >
                        <Checkbox 
                          checked={isSelected} 
                          disabled={!isAvailable}
                        />
                        <ListItemText 
                          primary={`E-${episode}${isUsed && !isSelected ? ' (Χρησιμοποιείται ήδη)' : ''}`}
                          sx={{
                            color: isUsed && !isSelected ? 'text.secondary' : 'text.primary',
                          }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors?.episodes && (
                  <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                    {errors.episodes}
                  </div>
                )}
              </FormControl>
            </Grid>
          )}

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              id="rec_notes-id"
              label={headerNames.rec_notes || "Σημειώσεις"}
              size="small"
              fullWidth
              multiline
              rows={4}
              value={state?.rec_notes || ""}
              onChange={(e) => updateField("rec_notes", e.target.value)}
              helperText={errors?.rec_notes || ""}
              error={!!errors?.rec_notes}
            />
          </Grid>
        </Grid>
      </Box>
  );
};

export default RecordingsForm;

